import { Alert, Stack } from "@mui/material";
import { useUpdateStoryMutation } from "Api/Mutations/Stories/useUpdateStoryMutation";
import { useStoryDetailQuery } from "Api/Queries/Stories/useStoryDetailQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { StoryForm } from "Components/Stories/Management/StoryForm";
import { StoryPreviewDialog } from "Components/Stories/Management/StoryPreviewDialog";
import { canEditStory } from "Components/Stories/Management/storyActionRules";
import { useNavigate } from "Navigation/useNavigate";
import { useState } from "react";
import { useParams } from "react-router";
import {
  type StoryFormData,
  toStoryFormData,
  toStorySaveRequest,
} from "Schemas/Stories/storyFormSchema";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { isStoryLockedWhileLiveError } from "Utils/Stories/storyErrors";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Stories.Management;

/** Edits a draft or expired story; a live one is shown read-only with the reason. */
export const StoryEditPage = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { publicID } = useParams<{ publicID: string }>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { data: detail, isLoading, isError } = useStoryDetailQuery(publicID);

  const {
    mutate: updateStory,
    isPending,
    isError: isSaveError,
    error,
  } = useUpdateStoryMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Form.SaveSuccess));
      navigate(appUrl("admin-stories"));
    },
  });

  const onSubmit = (data: StoryFormData) => {
    if (publicID) {
      updateStory({ publicID, story: toStorySaveRequest(data) });
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return <CenteredCircularProgress />;
    }

    if (isError || !detail) {
      return <ErrorText>{t(RESOURCES.LoadError)}</ErrorText>;
    }

    if (!canEditStory(detail.summary.state)) {
      return (
        <Stack gap={3} alignItems="flex-start">
          <Alert severity="info">{t(RESOURCES.Messages.LockedWhileLive)}</Alert>
          <PrimaryButton onClick={() => setIsPreviewOpen(true)}>
            {t(RESOURCES.Table.Preview)}
          </PrimaryButton>
          {isPreviewOpen && publicID && (
            <StoryPreviewDialog
              publicID={publicID}
              onClose={() => setIsPreviewOpen(false)}
            />
          )}
        </Stack>
      );
    }

    return (
      <StoryForm
        defaultValues={toStoryFormData(detail)}
        isPending={isPending}
        submitLabel={t(RESOURCES.Form.Save)}
        onSubmit={onSubmit}
        footer={
          isSaveError && (
            <Alert severity="error">
              {t(
                isStoryLockedWhileLiveError(error)
                  ? RESOURCES.Messages.LockedWhileLive
                  : RESOURCES.Form.SaveError,
              )}
            </Alert>
          )
        }
      />
    );
  };

  return (
    <AuthenticatedLayout title={t(RESOURCES.Form.EditTitle)}>
      <StyledPageWrapper $maxWidth="880px">{renderContent()}</StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default StoryEditPage;
